import dayjs from 'dayjs'
import React from 'react'

import {
  cartStatusTypes,
  commonAttributes,
  navigationPaths,
  tableColumnDefaultOption
} from 'app/config'

export const activeCarePlans = {
  standard: 'Active Care – Standard',
  flexible: 'Flexible Active Care',
  drop_in: 'Drop-in Care'
}

export const activeCareEnrolmentStatuses = {
  enrolled: 'Enrolled',
  not_enrolled: 'Paid, not enrolled',
  awaiting_payment: 'Awaiting payment'
}

const formatDate = (value) => (value ? dayjs(value).format('DD MMM, YYYY') : '')

const createdDate = {
  ...commonAttributes.date,
  name: 'created',
  label: 'Order Date',
  options: {
    ...commonAttributes.date.options,
    filterKey: 'created_date',
    customBodyRender: formatDate
  }
}

const centre = {
  name: 'centre_name',
  label: 'Centre',
  options: { ...tableColumnDefaultOption.options, filterKey: 'centre' }
}

const parent = {
  name: 'parent_name',
  label: 'Parent Name',
  options: { ...tableColumnDefaultOption.options, filterKey: 'parent_name' }
}

const parentNumber = {
  name: 'parent_phone_number',
  label: 'Parent Number',
  options: {
    ...tableColumnDefaultOption.options,
    filterKey: 'parent_phone_number'
  }
}

const child = {
  name: 'student_name',
  label: 'Child',
  options: { ...tableColumnDefaultOption.options, filterKey: 'student_name' }
}

const plan = {
  name: 'plan',
  label: 'Plan',
  widget: 'select',
  widgetFilter: true,
  isValueKey: true,
  valueLabelMappings: activeCarePlans,
  options: {
    ...tableColumnDefaultOption.options,
    filterKey: 'plan',
    customBodyRender: (value) => activeCarePlans[value] || value
  }
}

const details = {
  name: 'label',
  label: 'Details',
  options: { ...tableColumnDefaultOption.options, disableFilter: true }
}

const batch = {
  name: 'batch_name',
  label: 'Batch',
  options: { ...tableColumnDefaultOption.options, filterKey: 'batch' }
}

const enrolmentStatus = {
  name: 'enrolment_status',
  label: 'Enrolment',
  options: {
    ...tableColumnDefaultOption.options,
    disableFilter: true,
    customBodyRender: (value) => activeCareEnrolmentStatuses[value] || value
  }
}

const PAID_STATUSES = ['paid', 'completed']
const UNDELIVERED = ['failed', 'rejected']

// Monthly reminder WhatsApps for a link: the dates they went out, and
// "Stopped, paid" once the parent has paid. Drop-in Care is never reminded.
export const reminderSummary = (row) => {
  if (!row) return ''
  if (row.plan === 'drop_in') return 'Not sent for Drop-in'
  const sent = (row.reminders || []).map(
    (r) =>
      dayjs(r.sent_on).format('DD MMM') +
      (UNDELIVERED.includes(r.status) ? ' (not delivered)' : '')
  )
  const paid = PAID_STATUSES.includes(row.status)
  if (!sent.length) return paid ? 'Not needed, paid' : 'None yet'
  return paid ? `${sent.join(', ')} · Stopped, paid` : sent.join(', ')
}

const withReminders = (rows) => ({
  name: 'reminders',
  label: 'Reminders Sent',
  options: {
    ...tableColumnDefaultOption.options,
    disableFilter: true,
    customBodyRenderLite: (dataIndex) => reminderSummary(rows()?.[dataIndex])
  }
})

// Parent name links to the parent dashboard, as on the other Orders tabs.
const withParentLink = (rows) => ({
  ...parent,
  options: {
    ...parent.options,
    customBodyRenderLite: (dataIndex) => {
      const row = rows()?.[dataIndex]
      return row?.parent ? (
        <a href={`${navigationPaths.parentdashboard}?parent=${row.parent}`}>
          {row.parent_name}
        </a>
      ) : (
        row?.parent_name || null
      )
    }
  }
})

export const orderColumns = (rows) => [
  createdDate,
  centre,
  withParentLink(rows),
  parentNumber,
  child,
  plan,
  details,
  batch,
  {
    name: 'total_amount',
    label: 'Amount (incl. GST)',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) =>
        value ? <span>{parseFloat(value).toFixed(2)}</span> : null
    }
  },
  {
    name: 'status',
    label: 'Payment Status',
    widget: 'select',
    widgetFilter: true,
    isValueKey: true,
    valueLabelMappings: cartStatusTypes,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'status',
      customBodyRender: (value) => cartStatusTypes[value] || value
    }
  },
  {
    name: 'payment_link',
    label: 'Payment Link',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) =>
        value ? (
          <a target="_blank" rel="noopener noreferrer" href={value}>
            {value}
          </a>
        ) : null
    }
  },
  withReminders(rows),
  enrolmentStatus,
  {
    name: 'author_name',
    label: 'Created By',
    options: { ...tableColumnDefaultOption.options, disableFilter: true }
  }
]

// One row per paid link; Drop-in shows each visit date it enrolled.
export const enrolmentColumns = (rows) => [
  centre,
  child,
  withParentLink(rows),
  parentNumber,
  plan,
  details,
  batch,
  {
    name: 'enrolments',
    label: 'Enrolled For',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (enrolments) =>
        (enrolments || [])
          .map((e) =>
            e.start_date === e.end_date
              ? formatDate(e.start_date)
              : `${formatDate(e.start_date)} to ${formatDate(e.end_date)}`
          )
          .join(', ')
    }
  },
  {
    name: 'enrolments',
    label: 'Status',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (enrolments) =>
        [...new Set((enrolments || []).map((e) => e.status))].join(', ')
    }
  },
  { ...createdDate, label: 'Booked On' }
]
